import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LanguageService } from './language.service';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class JournalLanguageService {
  private currentJournalSubject = new BehaviorSubject<any>(null);
  public currentJournal$ = this.currentJournalSubject.asObservable();

  private isInJournalContextSubject = new BehaviorSubject<boolean>(false);
  public isInJournalContext$ = this.isInJournalContextSubject.asObservable();

  private journalLoadingSubject = new BehaviorSubject<boolean>(false);
  public journalLoading$ = this.journalLoadingSubject.asObservable();

  // Flag to indicate if language change is triggered by single language journal
  private isLanguageChangedBySingleLanguage = false;

  constructor(
    private languageService: LanguageService,
    private apiService: ApiService
  ) {}

  /**
   * Set loading state for journal/book
   */
  setJournalLoading(loading: boolean): void {
    this.journalLoadingSubject.next(loading);
    if (loading) {
      // Loading başladığında, journal context'inin var olacağını belirt
      this.isInJournalContextSubject.next(true);
    }
  }

  /**
   * Set the current journal/book context
   * @param journalOrBook The journal/book DTO or null when leaving context
   */
  setCurrentJournal(journalOrBook: any): void {
    this.currentJournalSubject.next(journalOrBook);
    this.isInJournalContextSubject.next(!!journalOrBook);
    this.journalLoadingSubject.next(false);
    
    // If single language is configured, force that language
    if (journalOrBook?.SingleLanguage && journalOrBook.SingleLanguageId) {
      const targetLanguage = this.getLanguageCode(journalOrBook.SingleLanguageId);
      const currentLanguage = this.languageService.getCurrentLanguage();
      
      // Only change language if it's different from current
      if (targetLanguage && targetLanguage !== currentLanguage) {
        this.isLanguageChangedBySingleLanguage = true;
        this.languageService.setLanguage(targetLanguage);
        
        // Reset flag after a short delay
        setTimeout(() => {
          this.isLanguageChangedBySingleLanguage = false;
        }, 200);
      }
    }
  }

  /**
   * Check if the last language change was triggered by single language journal
   */
  isLanguageChangeTriggeredBySingleLanguage(): boolean {
    return this.isLanguageChangedBySingleLanguage;
  }

  /**
   * Clear the current journal/book context
   */
  clearCurrentJournal(): void {
    this.currentJournalSubject.next(null);
    this.isInJournalContextSubject.next(false);
    this.journalLoadingSubject.next(false);
  }

  /**
   * Check if language switching is allowed
   */
  isLanguageChangeAllowed(): boolean {
    const currentJournal = this.currentJournalSubject.value;
    // Allow language change if no journal/book context OR if not single language
    return !currentJournal || !currentJournal.SingleLanguage;
  }

  /**
   * Check if we should show the language selector
   */
  shouldShowLanguageSelector(): boolean {
    const currentJournal = this.currentJournalSubject.value;
    const isLoading = this.journalLoadingSubject.value;
    const isInContext = this.isInJournalContextSubject.value;
    
    // Eğer journal/book context'inde değilsek, göster
    if (!isInContext) {
      return true;
    }
    
    // Eğer loading durumundaysa, gizle (çünkü single language olabilir)
    if (isLoading) {
      return false;
    }
    
    // Journal yüklendiyse, single language kontrolü yap
    return !currentJournal || !currentJournal.SingleLanguage;
  }

  /**
   * Get the current journal/book
   */
  getCurrentJournal(): any {
    return this.currentJournalSubject.value;
  }

  /**
   * Map SingleLanguageId to language code
   * @param languageId The language ID (1 = Turkish, 2 = English)
   */
  private getLanguageCode(languageId: number): string | null {
    switch (languageId) {
      case 1:
        return 'tr';
      case 2:
        return 'en';
      default:
        return null;
    }
  }
}
